import { z } from 'zod';
import type { ShapeManifest } from '../../../../lib/diagram/shape-manifest.types';
import { renderTagBadgesSvg } from '../../../../lib/diagram/tagBadgeLayout';
import {
  escapeSvgAttr,
  formatSvgNumber,
  renderShapeLabel,
  resolveShapePresentation,
} from '../../../diagram/shapes/svgRenderer.utils';
import { resolveFlowchartTextLayout } from '../../../diagram/shapes/flowchartTextLayout';
import { AI_FLOWCHART_SHAPES, AI_FLOWCHART_SHAPE_IDS, type AiFlowchartShapeId } from './FlowchartShapes.defs';

export type ProcessActivityType = 'task' | 'sub-process' | 'call-activity' | 'transaction';
export type GatewayType = 'exclusive' | 'parallel' | 'inclusive';
export type TerminatorType = 'start' | 'end';

/** Process status values for workflow tracking */
export type ProcessStatus = 'draft' | 'active' | 'deprecated';

/** Trigger types for start events */
export type TriggerType = 'manual' | 'scheduled' | 'event';

/** Outcome types for end events */
export type OutcomeType = 'success' | 'failure' | 'cancelled';

type FlowchartVariant = AiFlowchartShapeId;

const FLOWCHART_DEFAULTS = {
  fill: '#F8FAFC',
  stroke: '#1E293B',
  strokeWidth: 1.6,
  textColor: '#0F172A',
  fontSize: 12,
  fontFamily: 'Inter, sans-serif',
};

const TEXT_PADDING = 12;

const resolveCanvasFontStyle = (fontWeight?: string, fontStyle?: string): string => {
  const weight = fontWeight?.toLowerCase() ?? '';
  const style = fontStyle?.toLowerCase() ?? '';
  const isBold = weight === 'bold' || weight === '700' || style.includes('bold');
  const isItalic = style.includes('italic');
  if (isBold && isItalic) return 'bold italic';
  if (isBold) return 'bold';
  if (isItalic) return 'italic';
  return 'normal';
};

const clampDimension = (value: number | undefined, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  return fallback;
};

// Directly resolve AI shape definition from registry

const renderFlowchartShapeSVG = (
  variant: FlowchartVariant,
  dimensions: { width: number; height: number },
  args: {
    viewModel: { width?: number; height?: number; style?: Record<string, unknown> };
    props: z.infer<typeof flowchartShapeProps> & { attrs?: Record<string, unknown> };
  },
): string => {
  const def = AI_FLOWCHART_SHAPES[variant];
  const width = clampDimension(args.viewModel?.width, dimensions.width);
  const height = clampDimension(args.viewModel?.height, dimensions.height);
  const presentation = resolveShapePresentation(args.props, args.viewModel, { ...FLOWCHART_DEFAULTS, strokeWidth: def.strokeWidth });

  const dashAttr = presentation.dashEnabled && presentation.dash && presentation.dash.length > 0
    ? ` stroke-dasharray="${presentation.dash.join(',')}"`
    : '';

  const shared = `fill="${escapeSvgAttr(presentation.fill)}" stroke="${escapeSvgAttr(presentation.stroke)}" stroke-width="${formatSvgNumber(presentation.strokeWidth)}" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"${dashAttr}`;
  const fillOnly = `fill="${escapeSvgAttr(presentation.fill)}" stroke="none" vector-effect="non-scaling-stroke"`;
  const strokeOnly = `fill="none" stroke="${escapeSvgAttr(presentation.stroke)}" stroke-width="${formatSvgNumber(presentation.strokeWidth)}" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"${dashAttr}`;

  const scaleX = def.width > 0 ? width / def.width : 1;
  const scaleY = def.height > 0 ? height / def.height : 1;
  const transformAttr =
    scaleX === 1 && scaleY === 1
      ? ''
      : ` transform="scale(${formatSvgNumber(scaleX)} ${formatSvgNumber(scaleY)})"`;

  const shapeMarkup =
    `<g${transformAttr}>` +
    (def.fillPaths && def.fillPaths.length > 0
      ? def.fillPaths.map((d) => `<path d="${escapeSvgAttr(d)}" ${fillOnly} fill-rule="evenodd" clip-rule="evenodd" />`).join('')
      : '') +
    def.paths
      .map((d) => `<path d="${escapeSvgAttr(d)}" ${(def.fillPaths && def.fillPaths.length > 0) ? strokeOnly : shared} fill-rule="evenodd" clip-rule="evenodd" />`)
      .join('') +
    `</g>`;

  const layout = resolveFlowchartTextLayout(args.props.attrs?.flowchartTextLayout, {
    text: presentation.label ?? '',
    width: Math.max(0, width - TEXT_PADDING * 2),
    fontSize: presentation.fontSize,
    fontFamily: presentation.fontFamily,
    fontStyle: resolveCanvasFontStyle(presentation.fontWeight, presentation.fontStyle),
    fontWeight: presentation.fontWeight,
  });
  const labelMarkup = renderShapeLabel(presentation, width, height, TEXT_PADDING, {
    layout,
    lineHeight: 1,
  });

  return shapeMarkup + labelMarkup + renderTagBadgesSvg(args.props, height);
};

const flowchartShapeProps = z.object({
  // Core display
  label: z.string().optional(),

  // Process metadata (Tier 1 & 2 properties)
  description: z.string().optional(),
  owner: z.string().optional(),
  status: z.enum(['draft', 'active', 'deprecated']).optional(),
  duration: z.string().optional(), // Free-form: "2 hours", "3 days"

  // Shape-specific metadata
  triggerType: z.enum(['manual', 'scheduled', 'event']).optional(), // For start events
  outcomeType: z.enum(['success', 'failure', 'cancelled']).optional(), // For end events

  // Extended properties (Tier 3)
  sla: z.string().optional(),
  systems: z.array(z.string()).optional(), // Systems involved
  tags: z.array(z.string()).optional(),
  linkedDiagramId: z.string().optional(), // For sub-process drill-down

  // Visual styling
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),

  // Legacy attrs container for backward compatibility
  attrs: z.record(z.string(), z.any()).optional(),
});

export type FlowchartShapeProps = z.infer<typeof flowchartShapeProps>;

const createFlowchartShapeManifest = (
  id: FlowchartVariant,
  dimensions: { width: number; height: number }
): ShapeManifest<FlowchartShapeProps> => ({
  id,
  version: '1.0.0',
  category: 'flowchart',
  propsSchema: flowchartShapeProps,
  defaultProps: () => ({}),
  defaultDimensions: dimensions,
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: true,
    connectable: true,
  },
  renderers: {
    svg: (args) => renderFlowchartShapeSVG(id, dimensions, args),
  },
});

export const FlowchartShapeManifests: ShapeManifest<FlowchartShapeProps>[] = [
  ...AI_FLOWCHART_SHAPE_IDS.map((id) => {
    const def = AI_FLOWCHART_SHAPES[id];
    return createFlowchartShapeManifest(id, { width: def.width, height: def.height });
  }),
];
